import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { conf } from '../../../../domain/services/configService';
import { adService } from '../../../../domain/services/adService';
import { useBreakpoint } from '../../../../hooks/useBreakpoint';
import { gptService } from '../../../../domain/services/gptService';
import { userActivityService } from '../../../../domain/services/userActivityService';
import { useInterval } from '../../../../hooks/useInterval';
import { AdInfo } from './AdInfo';
import { domainService } from '../../../../domain/services/domainService';
import { AdFake } from './AdFake';

export const Ad = ({ id, content, refresh, collapseEmptyDiv }) => {
  if (content?.noAds && !id.startsWith('blank_template_')) {
    return null;
  }
  const [fakeAdValues, setFakeAdValues] = useState({
    width: null,
    height: null
  });
  const showFakeAd = !!fakeAdValues.width && !!fakeAdValues.height;

  const [breakpoint, previousBreakpoint] = useBreakpoint();
  const adSlot = useRef(null);
  const adEl = useRef(null);

  const containerId = content ? `gpt--${id}--${content.id}` : `gpt--${id}`;
  const adBaseId = id.replace(/__.*/, '');

  const mustShowAd = adService.isShowableOn(adBaseId, breakpoint.name);

  const destroy = () => {
    if (adSlot.current) {
      gptService.destroySlot(adSlot.current);
      adSlot.current = null;
    }
  };

  const create = () => {
    if (mustShowAd && !adSlot.current) {
      const allAvailableSizes = adService.getAllAvailableSizes(adBaseId);
      gptService.pushSlot(() => {
        const adUnitPath = adService.getAdUnitPath(content);
        const targeting = adService.getTargeting(content, null, adBaseId);
        adSlot.current = window.googletag.defineSlot(
          adUnitPath,
          allAvailableSizes,
          containerId
        );

        Object.keys(targeting).forEach(key =>
          adSlot.current.setTargeting(key, targeting[key])
        );

        if (collapseEmptyDiv) {
          adSlot.current.setCollapseEmptyDiv();
        }

        adSlot.current.addService(window.googletag.pubads());

        // Add size mapping for responsive ads
        const sizeMapping = gptService.getSizeMapping(
          adService.getBreakpointSizes(adBaseId)
        );

        if (sizeMapping) {
          adSlot.current.defineSizeMapping(sizeMapping);
        }

        // if (gptService.isDisplayAlreadyExecuted()) {
        gptService.display(adSlot.current);
        // }
      });
    }
  };

  // Create slot only once between breakpoint changes.
  // This means that if a slot has no sizes for mobile, it won't be created
  // until breakpoint is tablet or larger.
  useLayoutEffect(create, [breakpoint.name]);

  // Destroy slot on unmount.
  useLayoutEffect(() => {
    return () => {
      destroy();
    };
  }, []);

  /*
  Refresh adSlot when breakpoint changes

  There are different approaches to breakpoint changes:

  1)Destroy the adSlot: it's the safest and easiest approach, but is slower
    and doesn't take advantage of faster refresh() function. It makes
    individual requests for each adSlot.

  2)Refresh the adSlot: it's faster, but it calls refresh for adSlots not
    already rendered. I think this is not an issue, because lazy-loading
    takes cares of rendering only what's in the viewport.
    Lazy-loading ensures that "active view rate" is not negatively affected
    by rendering ads not in the viewport.

  At this moment, we are using option #2.

  Feel free to change behavior to option #1 if needed.
*/
  useEffect(() => {
    if (
      adSlot.current &&
      previousBreakpoint &&
      adService.isShowableOn(adBaseId, breakpoint.name)
    ) {
      gptService.refreshBreakpoint(adSlot.current);
    }
  }, [breakpoint.name]);

  // Refresh adSlot every X seconds, if configured to do so.
  const refreshRate = conf.ad_refresh_rate * 1000;
  useInterval(() => {
    // Do not execute anything if tab is hidden or browser minimized.
    // If browser is not the main window, it can be in the background but also
    // in another monitor, so it's not considered hidden.
    // @see https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API
    const slotId = adSlot.current ? adSlot.current.getSlotElementId() : null;
    const visible = slotId
      ? gptService.getSlotMetadata(slotId, 'visible')
      : false;
    if (
      visible &&
      refresh &&
      refreshRate &&
      !document.hidden &&
      userActivityService.isActive()
    ) {
      const firstVisible = gptService.getSlotMetadata(slotId, 'firstVisible');
      // Do not refresh an adSlot until enough time has elapsed since it
      // debuted on screen: refreshRate - 2 seconds.
      // We subtract 2 seconds because this interval starts running before
      // "firstVisible" is set.
      const debutedOnScreenEnoughTimeAgo =
        firstVisible && firstVisible + (refreshRate - 2000) < Date.now();
      if (debutedOnScreenEnoughTimeAgo) {
        gptService.refresh(adSlot.current);
      }
    }
  }, refreshRate);

  // Get responsive styles. Set "isCollapsable" to true if this ad
  // is allowed to collapse when slot is not filled. The reason is that
  // GPT on iPhone (and desktop browsers seem affected also) has a bug
  // that collapses all non-filled banners even though only one banner is
  // set to do it. GPT sets "display:none" on the div tag, but setting
  // "isCollapsable" to false sets an "! important" and avoids that CSS declaration
  // to be applied.
  const isCollapsable = collapseEmptyDiv || showFakeAd;
  const stylesWithMediaQueries = adService.getStylesWithMediaQueries(adBaseId, {
    isCollapsable
  });
  const styles = {
    main: css`
      overflow: hidden;
      ${stylesWithMediaQueries};
    `
  };

  // Listen for empty ad slots on stage and local environments.
  useEffect(() => {
    const emptyAdSlotListener = event => {
      const { slotId } = event.detail;
      if (slotId === containerId) {
        const breakpointSizes = adService.getBreakpointSizes(adBaseId);
        const currentBreakpointSizes = breakpointSizes[breakpoint.name];
        const firstSizes = Array.isArray(currentBreakpointSizes[0])
          ? currentBreakpointSizes[0]
          : currentBreakpointSizes;
        if (Array.isArray(firstSizes)) {
          const width = firstSizes[0];
          const height = firstSizes[1];
          if (width && height) {
            setFakeAdValues({ width, height });
          }
        }
      }
    };

    if (conf.isStage || domainService.isLocalDomain(window.location.hostname)) {
      document.addEventListener('adSlotEmpty', emptyAdSlotListener);
      return () =>
        document.removeEventListener('adSlotEmpty', emptyAdSlotListener);
    }

    // Return noop
    return () => {};
  }, [breakpoint.name]);

  return (
    <>
      <AdInfo adBaseId={adBaseId} containerId={containerId} content={content} />
      {showFakeAd && (
        <div css={styles.main}>
          <AdFake width={fakeAdValues.width} height={fakeAdValues.height} />
        </div>
      )}
      <div
        ref={adEl}
        id={containerId}
        css={styles.main}
        style={showFakeAd ? { display: 'none' } : null}
      />
    </>
  );
};

Ad.defaultProps = {
  id: undefined,
  content: undefined,
  refresh: true,
  collapseEmptyDiv: false
};

Ad.propTypes = {
  id: PropTypes.string,
  content: PropTypes.objectOf(PropTypes.any),
  refresh: PropTypes.bool,
  collapseEmptyDiv: PropTypes.bool
};
